/**
 * Typed feature flag keys.
 *
 * These must match the `key` values in backend/src/config/featureFlagRegistry.ts.
 * Using this constant gives IDE autocomplete and compile-time safety.
 *
 * Usage:
 *   import { FeatureFlags } from "shared/featureFlags";
 *   const enabled = useFeatureFlag(FeatureFlags.DirectorySlideout);
 */
export const FeatureFlags = {
  DirectorySlideout: "enable-directory-slideout",
  DirectoryV2: "directory_v2",
  FormsInBroadcasts: "forms-in-broadcasts-enabled",
  FormsInWidgets: "forms-in-widgets-enabled",
  MediaGalleryWidget: "mediaGalleryWidget",
  MembershipLink: "can-enable-membership-link",
  EmailActivityLogs: "email-activity-logs-enabled",
  AdvancedImageControls: "advanced-image-controls",
  MembershipTiers: "membership-tiers",
  MembershipHub: "membership_hub",
  Tripper: "tripper-enabled",
  ListingFreshness: "listing_freshness_feature",
  StorefrontRenewableFees: "storefront-renewable-fees",
  StorefrontEft: "enableEFTStorefront",
  StorefrontMerchProduct: "enableMerchProductStorefront",
  StorefrontV2: "storefront-v2",
  StorefrontSendToPay: "storefront-send-to-pay",
  StorefrontAccountingControls: "storefront-accounting-controls",
  StorefrontReportBuilder: "storefront-report-builder",
  StorefrontEnablePaymentMethods: "storefrontEnablePaymentMethods",
  EventsProductType: "eventsProductType",
  EventsOnHome: "events-on-home",
  PurchasesOnHome: "purchases-on-home",
  EnableTestStorefront: "enableTestStorefront",
  StorefrontPages: "enableStorefrontPages",
  OperatorWidgetListingFilter: "operator-widget-listing-filter",
  CustomListsV2: "custom_lists_v2",
  MemberLists: "member_lists",
  OperatorMapV2: "operatorMapV2",
  StorefrontDynamicFees: "storefront-dynamic-fees",
  ConditionalQuestions: "conditional-questions",
  InquiryWidgetNotificationEmails: "inquiry-widget-notification-emails",
  MembershipLedger: "membership_ledger",
  MembershipRenewal: "membership_renewal",
  RenewalEngineOwnedTags: "renewal_engine_owned_tags",
  OrgLevelEmailCustomizationSettings: "orgLevelemailCustomizationSettings",
  CustomEmailBody: "customEmailBody",
  WidgetEmbedActivityLog: "widget-embed-activity-log",
  DirectoryBulkInvite: "directory_bulk_invite",
  EnableBulkSelect: "enableBulkSelect",
  ListingAudiences: "listing_audiences",
  BulkSelectInCustomLists: "bulkSelectInCustomLists",
  BroadcastShareTeaser: "broadcast-share-teaser",
  OrgLevelGlobalSearch: "orgLevelGlobalSearch",
  CustomEmailDomain: "customEmailDomain",
  RequireBookingApprovalMode: "requireBookingApprovalMode",
  OptionalCardPostalCode: "optionalCardPostalCode",
  OnboardingTestDataTool: "onboardingTestDataTool",
  CustomEmailsToMembers: "customEmailsToMembers",
  Promotions: "promotions",
  MembershipMultiListing: "membership_multi_listing",
  Workbench: "workbench",
  PartnerEventSharing: "partnerEventSharing",
  StorefrontFeeCalculator: "storefrontFeeCalculator",
  CheckoutRequirementsChecklist: "checkoutRequirementsChecklist",
  StorefrontGettingStarted: "storefrontGettingStarted",
  RenewableFeeAddOns: "renewableFeeAddOns",
  PerBusinessAnswers: "perBusinessAnswers",
} as const;

export type FeatureFlagKey = (typeof FeatureFlags)[keyof typeof FeatureFlags];
